import React, { useState } from "react";
import { X, Flag, Shield } from "lucide-react";
import apiClient from "../api/client";

export default function ReportBlockModal({
  open,
  onClose,
  targetUserId,
  targetUserName,
  matchId,
  onBlocked,
}) {
  const [mode, setMode] = useState("report"); // "report" | "block"
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  if (!open) return null;

  const reset = () => {
    setMode("report");
    setReason("");
    setDetails("");
    setLoading(false);
    setErrorMsg("");
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetUserId) return;

    setLoading(true);
    setErrorMsg("");

    try {
      if (mode === "block") {
        await apiClient.post("/users/block", {
          target_user_id: targetUserId,
          match_id: matchId || null,
          reason: reason || null,
        });
        onBlocked?.();
        handleClose();
        return;
      }

      // report mode
      if (!reason.trim()) {
        setErrorMsg("Please select or enter a reason to report.");
        setLoading(false);
        return;
      }

      await apiClient.post("/users/report", {
        reported_user_id: targetUserId,
        match_id: matchId || null,
        reason,
        details: details || null,
      });

      handleClose();
    } catch (err) {
      console.error("ReportBlockModal error:", err);
      setErrorMsg(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
      setLoading(false);
    }
  };

  const reasonPresets = [
    "Harassment or hate",
    "Spam or scam",
    "Fake or suspicious profile",
    "Inappropriate content",
    "Something else",
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 rounded-2xl bg-gray-900 border border-red-500/40 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div className="flex items-center space-x-2">
            {mode === "report" ? (
              <Flag className="w-4 h-4 text-red-400" />
            ) : (
              <Shield className="w-4 h-4 text-red-400" />
            )}
            <div className="text-xs uppercase tracking-[0.2em] text-red-400">
              {mode === "report" ? "Report User" : "Block User"}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-gray-800 text-gray-400 hover:text-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <p className="text-sm text-gray-300">
              You are about to{" "}
              <span className="font-semibold">
                {mode === "report" ? "report" : "block"}
              </span>{" "}
              <span className="text-red-300">
                {targetUserName || "this user"}
              </span>
              .
            </p>
            {mode === "block" ? (
              <p className="mt-1 text-xs text-gray-400">
                Blocking will remove them from your matches and prevent them
                from contacting you in the future.
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-400">
                Reports are reviewed for safety and community guideline
                violations.
              </p>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex items-center space-x-2 text-xs bg-gray-950 border border-gray-800 rounded-full p-1">
            <button
              type="button"
              onClick={() => setMode("report")}
              className={[
                "flex-1 px-2 py-1 rounded-full text-center",
                mode === "report"
                  ? "bg-red-500 text-white"
                  : "text-gray-400 hover:text-gray-200",
              ].join(" ")}
            >
              Report
            </button>
            <button
              type="button"
              onClick={() => setMode("block")}
              className={[
                "flex-1 px-2 py-1 rounded-full text-center",
                mode === "block"
                  ? "bg-red-500 text-white"
                  : "text-gray-400 hover:text-gray-200",
              ].join(" ")}
            >
              Block
            </button>
          </div>

          {/* Reason section */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-300">
              Reason
            </div>
            <div className="flex flex-wrap gap-1">
              {reasonPresets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setReason(preset)}
                  className={[
                    "px-2 py-1 rounded-full text-[11px] border",
                    reason === preset
                      ? "border-red-500 bg-red-500/20 text-red-200"
                      : "border-gray-700 text-gray-300 hover:border-gray-500",
                  ].join(" ")}
                >
                  {preset}
                </button>
              ))}
            </div>
            <textarea
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Add more details (optional)..."
              className="w-full mt-1 text-sm rounded-xl bg-gray-950 border border-gray-800 px-3 py-2 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </div>

          {errorMsg && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded-xl px-3 py-2">
              {errorMsg}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-xs text-gray-400 hover:text-gray-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-1.5 text-xs font-medium rounded-full bg-red-500 hover:bg-red-400 text-white shadow-md shadow-red-500/30 disabled:opacity-60"
            >
              {loading
                ? "Submitting…"
                : mode === "report"
                ? "Submit Report"
                : "Block User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
